import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import {
  Alert,
  Button,
  Col,
  CustomInput,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner
} from 'reactstrap'
import { createRewardAccelerator, getRewardsAdminErrorMessage, updateRewardAccelerator } from '../../../../helpers/api/rewardsAdmin'
import { Toast } from '../../../UI/toast/Toast'
import { ACCELERATOR_TYPES } from '../rewardsAdminUtils'

// CodeResponse::RESOURCE_EXIST del backend: ya hay un acelerador con ese
// código para el aliado (409).
const RESOURCE_EXIST = 'H0011'

const emptyForm = () => ({
  partnerId: '',
  code: '',
  type: 'service',
  name: '',
  description: '',
  hcAmount: '',
  raffleTickets: '0',
  monthlyCap: '',
  validFrom: moment().format('YYYY-MM-DD'),
  validTo: '',
  isActive: true
})

const isNonNegativeNumber = (text) => String(text).trim() !== '' && Number.isFinite(Number(text)) && Number(text) >= 0
const isInteger = (text, min) => /^\d+$/.test(String(text).trim()) && Number(text) >= min

const validate = (form, isNew) => {
  const errors = {}
  if (isNew && !form.partnerId) errors.partnerId = 'Elige el aliado.'
  if (isNew && !form.code.trim()) errors.code = 'Ingresa el código que envía el aliado.'
  if (!form.name.trim()) errors.name = 'Ingresa el nombre.'
  if (!isNonNegativeNumber(form.hcAmount)) errors.hcAmount = 'Ingresa 0 o más Coins.'
  if (!isInteger(form.raffleTickets, 0)) errors.raffleTickets = 'Ingresa un número entero de 0 o más.'
  if (form.monthlyCap !== '' && !isInteger(form.monthlyCap, 1)) errors.monthlyCap = 'Ingresa 1 o más, o déjalo vacío.'
  if (!form.validFrom) errors.validFrom = 'Ingresa la fecha de inicio.'
  if (form.validTo && form.validFrom && form.validTo < form.validFrom) errors.validTo = 'No puede ser anterior al inicio.'
  return errors
}

// HO-TASK-289: alta y edición de un acelerador (acción de un aliado que da
// Coins y tickets). El aliado y el código no se cambian después de crearlo.
const AcceleratorModal = ({ isOpen, accelerator, partners, onClose, onSaved }) => {
  const isNew = !accelerator
  const [form, setForm] = useState(emptyForm)
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState(null)

  useEffect(() => {
    if (!isOpen) return
    setForm(
      accelerator
        ? {
          partnerId: String(accelerator.partner?.id || ''),
          code: accelerator.code,
          type: accelerator.type,
          name: accelerator.name,
          description: accelerator.description || '',
          hcAmount: String(accelerator.hc_amount),
          raffleTickets: String(accelerator.raffle_tickets),
          monthlyCap: accelerator.monthly_cap_per_user === null ? '' : String(accelerator.monthly_cap_per_user),
          validFrom: accelerator.valid_from || '',
          validTo: accelerator.valid_to || '',
          isActive: accelerator.is_active
        }
        : emptyForm()
    )
    setTouched(false)
    setServerError(null)
  }, [isOpen, accelerator])

  const errors = validate(form, isNew)
  const hasErrors = Object.keys(errors).length > 0
  const showError = (field) => touched && !!errors[field]
  const update = (field) => ({ target }) =>
    setForm((current) => ({ ...current, [field]: target.type === 'checkbox' ? target.checked : target.value }))

  const save = async () => {
    setTouched(true)
    if (hasErrors) return
    const body = {
      type: form.type,
      name: form.name.trim(),
      description: form.description.trim() || null,
      hc_amount: Number(form.hcAmount),
      raffle_tickets: Number(form.raffleTickets),
      monthly_cap_per_user: form.monthlyCap === '' ? null : Number(form.monthlyCap),
      valid_from: form.validFrom,
      valid_to: form.validTo || null,
      is_active: form.isActive
    }

    setSaving(true)
    setServerError(null)
    try {
      if (isNew) {
        await createRewardAccelerator({ ...body, partner_id: Number(form.partnerId), code: form.code.trim() })
      } else {
        await updateRewardAccelerator(accelerator.id, body)
      }
      Toast.fire({ type: 'success', title: isNew ? 'Acelerador creado.' : 'Acelerador guardado.' })
      onSaved()
    } catch (error) {
      const data = error?.data || error
      setServerError(
        data?.code === RESOURCE_EXIST
          ? 'Ese aliado ya tiene un acelerador con ese código.'
          : getRewardsAdminErrorMessage(error, 'No pudimos guardar el acelerador.')
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={saving ? undefined : onClose} centered size="lg">
      <ModalHeader toggle={saving ? undefined : onClose}>{isNew ? 'Nuevo acelerador' : `Editar ${accelerator.name}`}</ModalHeader>
      <ModalBody>
        {serverError && <Alert color="danger" className="fs--1">{serverError}</Alert>}
        <Row form>
          <Col md={6}>
            <FormGroup>
              <Label for="accelerator-partner" className="fs--1">Aliado</Label>
              <Input
                id="accelerator-partner"
                type="select"
                bsSize="sm"
                value={form.partnerId}
                disabled={!isNew}
                invalid={showError('partnerId')}
                onChange={update('partnerId')}
              >
                <option value="">Elige un aliado</option>
                {partners.map((partner) => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name}
                    {partner.status !== 'active' ? ' (inactivo)' : ''}
                  </option>
                ))}
              </Input>
              <FormFeedback>{errors.partnerId}</FormFeedback>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="accelerator-code" className="fs--1">Código</Label>
              <Input
                id="accelerator-code"
                bsSize="sm"
                maxLength={80}
                value={form.code}
                disabled={!isNew}
                invalid={showError('code')}
                onChange={update('code')}
              />
              <FormFeedback>{errors.code}</FormFeedback>
              <small className="text-600">El que envía el aliado en cada evento del webhook.</small>
            </FormGroup>
          </Col>
          <Col md={8}>
            <FormGroup>
              <Label for="accelerator-name" className="fs--1">Nombre</Label>
              <Input id="accelerator-name" bsSize="sm" maxLength={150} value={form.name} invalid={showError('name')} onChange={update('name')} />
              <FormFeedback>{errors.name}</FormFeedback>
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label for="accelerator-type" className="fs--1">Tipo</Label>
              <Input id="accelerator-type" type="select" bsSize="sm" value={form.type} onChange={update('type')}>
                {Object.entries(ACCELERATOR_TYPES).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col xs={12}>
            <FormGroup>
              <Label for="accelerator-description" className="fs--1">Descripción para el propietario (opcional)</Label>
              <Input id="accelerator-description" bsSize="sm" maxLength={255} value={form.description} onChange={update('description')} />
            </FormGroup>
          </Col>
          <Col sm={4}>
            <FormGroup>
              <Label for="accelerator-hc" className="fs--1">Coins por evento</Label>
              <InputGroup size="sm">
                <Input id="accelerator-hc" type="number" min={0} step="any" value={form.hcAmount} invalid={showError('hcAmount')} onChange={update('hcAmount')} />
                <InputGroupAddon addonType="append">
                  <InputGroupText>HC</InputGroupText>
                </InputGroupAddon>
                <FormFeedback>{errors.hcAmount}</FormFeedback>
              </InputGroup>
            </FormGroup>
          </Col>
          <Col sm={4}>
            <FormGroup>
              <Label for="accelerator-tickets" className="fs--1">Tickets de sorteo</Label>
              <Input
                id="accelerator-tickets"
                type="number"
                min={0}
                step={1}
                bsSize="sm"
                value={form.raffleTickets}
                invalid={showError('raffleTickets')}
                onChange={update('raffleTickets')}
              />
              <FormFeedback>{errors.raffleTickets}</FormFeedback>
            </FormGroup>
          </Col>
          <Col sm={4}>
            <FormGroup>
              <Label for="accelerator-cap" className="fs--1">Tope por propietario al mes</Label>
              <Input
                id="accelerator-cap"
                type="number"
                min={1}
                step={1}
                bsSize="sm"
                placeholder="Sin tope"
                value={form.monthlyCap}
                invalid={showError('monthlyCap')}
                onChange={update('monthlyCap')}
              />
              <FormFeedback>{errors.monthlyCap}</FormFeedback>
            </FormGroup>
          </Col>
          <Col sm={4}>
            <FormGroup>
              <Label for="accelerator-from" className="fs--1">Vigente desde</Label>
              <Input id="accelerator-from" type="date" bsSize="sm" value={form.validFrom} invalid={showError('validFrom')} onChange={update('validFrom')} />
              <FormFeedback>{errors.validFrom}</FormFeedback>
            </FormGroup>
          </Col>
          <Col sm={4}>
            <FormGroup>
              <Label for="accelerator-to" className="fs--1">Hasta (opcional)</Label>
              <Input
                id="accelerator-to"
                type="date"
                bsSize="sm"
                min={form.validFrom || undefined}
                value={form.validTo}
                invalid={showError('validTo')}
                onChange={update('validTo')}
              />
              <FormFeedback>{errors.validTo}</FormFeedback>
            </FormGroup>
          </Col>
          <Col sm={4} className="d-flex align-items-center">
            <FormGroup className="mb-3 mt-sm-3">
              <CustomInput
                type="switch"
                id="accelerator-active"
                label="Activo"
                checked={form.isActive}
                onChange={update('isActive')}
              />
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="falcon-default" size="sm" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button color="primary" size="sm" onClick={save} disabled={saving || (touched && hasErrors)}>
          {saving && <Spinner size="sm" className="mr-2" />}
          {isNew ? 'Crear acelerador' : 'Guardar'}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

AcceleratorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  accelerator: PropTypes.object,
  partners: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
}

export default AcceleratorModal
